import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import type { DateRangePreset } from '@/api/types/usage';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import type { UsageTab } from '@/components/UsageTracking/UsageBreakdownTabs';
import { UsageTrackingPage } from '@/components/UsageTracking/UsageTrackingPage';
import {
  DEFAULT_CODEBASE_SORT_BY,
  DEFAULT_CODEBASE_SORT_ORDER,
  DEFAULT_ENGAGEMENT_SORT_BY,
  DEFAULT_ENGAGEMENT_SORT_ORDER,
  DEFAULT_MACHINE_SORT_BY,
  DEFAULT_MACHINE_SORT_ORDER,
  DEFAULT_SERVICE_SORT_BY,
  DEFAULT_SERVICE_SORT_ORDER,
  DEFAULT_TOOL_SORT_BY,
  DEFAULT_TOOL_SORT_ORDER,
  DEFAULT_USER_SORT_BY,
  DEFAULT_USER_SORT_ORDER,
} from '@/components/UsageTracking/constants';
import { useUser } from '@/hooks/useUser';

const usageTabValues: [UsageTab, ...UsageTab[]] = [
  'by-tool',
  'by-user',
  'by-machine',
  'by-codebase',
  'by-service',
  'engagement',
];
const dateRangePresetValues: [DateRangePreset, ...DateRangePreset[]] = ['7d', '30d', '90d', 'custom'];

const sortOrderValues = ['asc', 'desc'] as const;

export const usageSearchSchema = z.object({
  preset: fallback(z.enum(dateRangePresetValues), '30d').default('30d'),
  startDate: fallback(z.string(), '').optional(),
  endDate: fallback(z.string(), '').optional(),
  codebase: fallback(z.string(), '').optional(),
  machine: fallback(z.string(), '').optional(),
  user: fallback(z.string(), '').optional(),
  team: fallback(z.string(), '').optional(),
  tab: fallback(z.enum(usageTabValues), 'by-tool').default('by-tool'),
  pageIndex: fallback(z.number(), 0).default(0),
  pageSize: fallback(z.number(), 10).default(10),
  engagementSortBy: fallback(z.string(), DEFAULT_ENGAGEMENT_SORT_BY).optional(),
  engagementSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_ENGAGEMENT_SORT_ORDER).optional(),
  toolSortBy: fallback(z.string(), DEFAULT_TOOL_SORT_BY).optional(),
  toolSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_TOOL_SORT_ORDER).optional(),
  userSortBy: fallback(z.string(), DEFAULT_USER_SORT_BY).optional(),
  userSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_USER_SORT_ORDER).optional(),
  codebaseSortBy: fallback(z.string(), DEFAULT_CODEBASE_SORT_BY).optional(),
  codebaseSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_CODEBASE_SORT_ORDER).optional(),
  serviceSortBy: fallback(z.string(), DEFAULT_SERVICE_SORT_BY).optional(),
  serviceSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_SERVICE_SORT_ORDER).optional(),
  machineSortBy: fallback(z.string(), DEFAULT_MACHINE_SORT_BY).optional(),
  machineSortOrder: fallback(z.enum(sortOrderValues), DEFAULT_MACHINE_SORT_ORDER).optional(),
});

export const Route = createFileRoute('/$organizationName/usage')({
  component: SecuredUsageTrackingPage,
  validateSearch: zodValidator(usageSearchSchema),
});

function SecuredUsageTrackingPage() {
  return (
    <LayoutDefault
      showBorder
      navbarTitle="Usage"
      header={
        <LayoutHeader>
          <LayoutHeaderTitle title="Usage" />
        </LayoutHeader>
      }
    >
      <UsageTrackingPageWithTitle />
    </LayoutDefault>
  );
}

function UsageTrackingPageWithTitle() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Usage`;
  }, [organizationDisplayName]);

  return <UsageTrackingPage />;
}
